import React from "react";
import { writeClientError } from "./ErrorLogger";

const isIgnoredBrowserNoise = (message?: string | null) => {
  if (!message) {
    return false;
  }

  return message.includes("ResizeObserver loop completed with undelivered notifications.")
    || message.includes("ResizeObserver loop limit exceeded");
};

export class GlobalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  private handleWindowError = (event: ErrorEvent) => {
    const message = event.message || event.error?.message;
    if (isIgnoredBrowserNoise(message)) {
      return;
    }

    writeClientError("client.window_error", event.error ?? new Error(event.message), {
      payloadSummary: event.filename
        ? `source=${event.filename}:${event.lineno}:${event.colno}`
        : undefined
    });
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reasonMessage = typeof event.reason === "string"
      ? event.reason
      : event.reason?.message;

    if (isIgnoredBrowserNoise(reasonMessage)) {
      return;
    }

    writeClientError("client.unhandled_rejection", event.reason, {
      payloadSummary: `url=${window.location.href}`
    });
  };

  private handleSecurityPolicyViolation = (event: SecurityPolicyViolationEvent) => {
    const blockedUri = event.blockedURI || "unknown";
    const directive = event.violatedDirective || event.effectiveDirective || "unknown";

    writeClientError(
      "client.security_policy_violation",
      new Error(`Blocked by CSP: ${directive} | blockedUri=${blockedUri}`),
      {
        payloadSummary: `documentUri=${event.documentURI} | source=${event.sourceFile ?? "unknown"}:${event.lineNumber}:${event.columnNumber}`,
        requestPath: window.location.pathname,
        responseBody: [
          `effectiveDirective=${event.effectiveDirective || "unknown"}`,
          `violatedDirective=${event.violatedDirective || "unknown"}`,
          `blockedURI=${blockedUri}`,
          `originalPolicy=${event.originalPolicy || "unknown"}`
        ].join(" | ")
      }
    );
  };

  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidMount() {
    window.addEventListener("error", this.handleWindowError);
    window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
    window.addEventListener("securitypolicyviolation", this.handleSecurityPolicyViolation);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleWindowError);
    window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
    window.removeEventListener("securitypolicyviolation", this.handleSecurityPolicyViolation);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    writeClientError("client.ui_error", error, {
      payloadSummary: `url=${window.location.href} | userAgent=${navigator.userAgent}`,
      responseBody: info.componentStack?.trim() || error.stack
    });
  }

  render() {
    if (this.state.hasError) {
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <h2>The application ran into an unexpected problem.</h2>

      <button style={{fontSize:"18px"}} onClick={() => window.location.reload()}>
        Reload application
      </button>

      <br /><br />

      <button
      style={{fontSize:"18px"}}
        onClick={() => {
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = "/login";
        }}
      >
        Go to login
      </button>
    </div>
  );
}
    return this.props.children;
  }
}
